import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  Search,
  Download,
  LogOut,
  X,
  Trash2,
  MessageCircle,
  Phone as PhoneIcon,
  Mail as MailIcon,
  Filter,
} from "lucide-react";
import { api, getToken, setToken, clearToken, formatApiErrorDetail } from "../lib/api";

const STATUS_VALUES = ["New", "Contacted", "In Review", "Converted", "Closed"];
const STATUS_COLORS = {
  New: "bg-[#B99A5B]/15 text-[#7E6630] border-[#B99A5B]/40",
  Contacted: "bg-blue-50 text-blue-800 border-blue-200",
  "In Review": "bg-amber-50 text-amber-800 border-amber-200",
  Converted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Closed: "bg-stone-100 text-stone-700 border-stone-300",
};

const WHATSAPP_BASE =
  "https://wa.me/91PHONE?text=Hello%20from%20Lex%20Polaris%20Team.%20This%20is%20regarding%20your%20enquiry.";

export default function AdminPage() {
  const [authed, setAuthed] = useState(Boolean(getToken()));
  const [checking, setChecking] = useState(Boolean(getToken()));

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then(() => {
        setAuthed(true);
        setChecking(false);
      })
      .catch(() => {
        clearToken();
        setAuthed(false);
        setChecking(false);
      });
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="text-charcoal/60 text-sm tracking-[0.18em] uppercase">Checking session…</div>
      </div>
    );
  }

  return authed ? (
    <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} />
  ) : (
    <LoginScreen onLogin={() => setAuthed(true)} />
  );
}

// ============================ Login ============================
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success("Welcome back.");
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col" data-testid="admin-login">
      <header className="border-b border-soft-grey bg-ivory/90 backdrop-blur-sm">
        <div className="container-px max-w-[1400px] mx-auto h-20 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl text-navy">Lex Polaris</Link>
          <Link to="/" className="text-xs tracking-[0.2em] uppercase text-charcoal/60 hover:text-navy transition-colors">
            ← Back to site
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-white border border-soft-grey p-9 md:p-12">
          <div className="overline mb-5">Admin Console</div>
          <h1 className="font-serif text-3xl md:text-4xl text-navy mb-3">Sign in</h1>
          <p className="text-charcoal/65 text-sm mb-9">Restricted access. Authorised personnel only.</p>
          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <label className="block">
              <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/60 mb-2">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors"
                placeholder="admin@lexpolaris.in"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/60 mb-2">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full bg-transparent border-b border-soft-grey focus:border-navy py-3 text-charcoal placeholder:text-charcoal/40 outline-none transition-colors"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="btn-primary w-full disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}

// ============================ Dashboard ============================
function Dashboard({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setItems(data);
    } catch (err) {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (statusFilter !== "All" && i.status !== statusFilter) return false;
      if (q) {
        const blob = `${i.name} ${i.phone} ${i.email} ${i.matter_type}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (dateFrom) {
        if (new Date(i.submitted_at) < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const end = new Date(dateTo);
        end.setHours(23, 59, 59, 999);
        if (new Date(i.submitted_at) > end) return false;
      }
      return true;
    });
  }, [items, search, statusFilter, dateFrom, dateTo]);

  const counts = useMemo(() => {
    const out = { Total: items.length };
    STATUS_VALUES.forEach((s) => { out[s] = items.filter((i) => i.status === s).length; });
    return out;
  }, [items]);

  const downloadExcel = (rows, filename) => {
    if (rows.length === 0) {
      toast.error("No enquiries to download.");
      return;
    }
    const data = rows.map((r) => {
      const dt = new Date(r.submitted_at);
      return {
        Name: r.name,
        Phone: r.phone,
        Email: r.email,
        "Matter Type": r.matter_type,
        Message: r.message || "",
        "Page Source": r.page_source || "",
        Status: r.status,
        "Admin Notes": r.admin_notes || "",
        "Submitted Date": dt.toLocaleDateString("en-IN"),
        "Submitted Time": dt.toLocaleTimeString("en-IN"),
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [
      { wch: 22 }, { wch: 16 }, { wch: 28 }, { wch: 22 }, { wch: 50 },
      { wch: 22 }, { wch: 14 }, { wch: 40 }, { wch: 14 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, filename);
    toast.success(`Downloaded ${rows.length} enquiries.`);
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      {/* HEADER */}
      <header className="border-b border-soft-grey bg-white sticky top-0 z-30">
        <div className="container-px max-w-[1400px] mx-auto h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-2xl text-navy">Lex Polaris</Link>
            <span className="hidden md:inline text-[11px] tracking-[0.22em] uppercase text-charcoal/55">Admin Console</span>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-charcoal/70 hover:text-navy transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="container-px max-w-[1400px] mx-auto py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-9">
          <div>
            <div className="overline mb-3">Enquiries</div>
            <h1 className="font-serif text-4xl md:text-5xl text-navy">All consultation enquiries</h1>
            <p className="text-charcoal/65 mt-2 text-sm">Search, filter, update status and export.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => downloadExcel(items, `lex-polaris-enquiries-all-${Date.now()}.xlsx`)}
              data-testid="admin-download-all"
              className="btn-outline inline-flex items-center gap-2"
            >
              <Download size={14} /> Download All
            </button>
            <button
              onClick={() => downloadExcel(filtered, `lex-polaris-enquiries-filtered-${Date.now()}.xlsx`)}
              data-testid="admin-download-filtered"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Download size={14} /> Download Filtered
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-soft-grey border border-soft-grey mb-9" data-testid="admin-stats">
          {["Total", ...STATUS_VALUES].map((k) => (
            <div key={k} className="bg-white p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-charcoal/55">{k}</div>
              <div className="font-serif text-3xl text-navy mt-1">{counts[k] ?? 0}</div>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div className="bg-white border border-soft-grey p-5 md:p-6 mb-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-4">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-charcoal/55 mb-2">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search"
                placeholder="Name, phone, email, matter type"
                className="w-full bg-ivory/60 border border-soft-grey pl-9 pr-3 py-2.5 text-sm outline-none focus:border-navy"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-charcoal/55 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-filter-status"
              className="w-full bg-ivory/60 border border-soft-grey px-3 py-2.5 text-sm outline-none focus:border-navy"
            >
              <option value="All">All</option>
              {STATUS_VALUES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-charcoal/55 mb-2">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="admin-filter-from"
              className="w-full bg-ivory/60 border border-soft-grey px-3 py-2.5 text-sm outline-none focus:border-navy"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase text-charcoal/55 mb-2">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="admin-filter-to"
              className="w-full bg-ivory/60 border border-soft-grey px-3 py-2.5 text-sm outline-none focus:border-navy"
            />
          </div>
          <div className="md:col-span-1">
            <button
              onClick={() => { setSearch(""); setStatusFilter("All"); setDateFrom(""); setDateTo(""); }}
              data-testid="admin-clear-filters"
              className="w-full inline-flex items-center justify-center gap-2 border border-soft-grey px-3 py-2.5 text-xs tracking-[0.16em] uppercase text-charcoal/70 hover:bg-ivory transition-colors"
              title="Clear filters"
            >
              <Filter size={12} /> Clear
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border border-soft-grey overflow-x-auto" data-testid="admin-table-wrapper">
          {loading ? (
            <div className="p-10 text-center text-charcoal/55 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-charcoal/55 text-sm" data-testid="admin-empty">
              No enquiries match the current filters.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ivory/60 border-b border-soft-grey">
                <tr className="text-left">
                  <Th>Submitted</Th>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Matter</Th>
                  <Th>Page</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const dt = new Date(row.submitted_at);
                  return (
                    <tr
                      key={row.id}
                      data-testid={`admin-row-${row.id}`}
                      className="border-b border-soft-grey/70 hover:bg-ivory/40 transition-colors"
                    >
                      <Td className="whitespace-nowrap text-charcoal/70 text-[13px]">
                        <div>{dt.toLocaleDateString("en-IN")}</div>
                        <div className="text-charcoal/50 text-xs">{dt.toLocaleTimeString("en-IN")}</div>
                      </Td>
                      <Td>
                        <div className="font-serif text-navy text-[15px]">{row.name}</div>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <a href={`tel:${row.phone}`} className="text-charcoal hover:text-navy inline-flex items-center gap-1 text-[13px]">
                              <PhoneIcon size={12} /> {row.phone}
                            </a>
                            <a
                              href={WHATSAPP_BASE.replace("PHONE", row.phone.replace(/\D/g, ""))}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="WhatsApp"
                              className="text-[#25D366] hover:opacity-80"
                              data-testid={`admin-whatsapp-${row.id}`}
                            >
                              <MessageCircle size={14} />
                            </a>
                          </div>
                          <a href={`mailto:${row.email}`} className="text-charcoal/75 hover:text-navy inline-flex items-center gap-1 text-[13px] break-all">
                            <MailIcon size={12} /> {row.email}
                          </a>
                        </div>
                      </Td>
                      <Td className="text-charcoal/80 text-[13px]">{row.matter_type}</Td>
                      <Td className="text-charcoal/60 text-[12px]">{row.page_source || "—"}</Td>
                      <Td>
                        <span className={`inline-block text-[10px] tracking-[0.18em] uppercase border px-2 py-1 ${STATUS_COLORS[row.status] || ""}`}>
                          {row.status}
                        </span>
                      </Td>
                      <Td className="text-right whitespace-nowrap">
                        <button
                          onClick={() => setActive(row)}
                          data-testid={`admin-open-${row.id}`}
                          className="text-[11px] tracking-[0.18em] uppercase text-navy border-b border-navy/30 hover:border-gold hover:text-gold transition-colors"
                        >
                          Open
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {active && (
        <EnquiryDrawer
          enquiry={active}
          onClose={() => setActive(null)}
          onUpdated={(updated) => {
            setItems((arr) => arr.map((i) => (i.id === updated.id ? updated : i)));
            setActive(updated);
          }}
          onDeleted={(id) => {
            setItems((arr) => arr.filter((i) => i.id !== id));
            setActive(null);
          }}
        />
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-charcoal/55 font-medium ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-4 align-top ${className}`}>{children}</td>
);

// ============================ Drawer ============================
function EnquiryDrawer({ enquiry, onClose, onUpdated, onDeleted }) {
  const [status, setStatus] = useState(enquiry.status);
  const [notes, setNotes] = useState(enquiry.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dt = new Date(enquiry.submitted_at);
  const phoneDigits = (enquiry.phone || "").replace(/\D/g, "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/enquiries/${enquiry.id}`, {
        status,
        admin_notes: notes,
      });
      toast.success("Enquiry updated.");
      onUpdated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    setDeleting(true);
    try {
      await api.delete(`/enquiries/${enquiry.id}`);
      toast.success("Enquiry deleted.");
      onDeleted(enquiry.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-drawer">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-deep-ink/60 backdrop-blur-sm"
      />
      <aside className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="px-7 py-5 border-b border-soft-grey flex items-center justify-between">
          <div>
            <div className="overline">Enquiry</div>
            <div className="font-serif text-2xl text-navy mt-1">{enquiry.name}</div>
          </div>
          <button onClick={onClose} className="text-charcoal/60 hover:text-navy" data-testid="admin-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 py-6 space-y-6">
          <Detail label="Phone">
            <div className="flex items-center gap-3">
              <a href={`tel:${enquiry.phone}`} className="text-navy hover:text-gold inline-flex items-center gap-2">
                <PhoneIcon size={14} /> {enquiry.phone}
              </a>
              <a
                href={`https://wa.me/91${phoneDigits}?text=Hello%20from%20Lex%20Polaris%20Team.`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-xs"
              >
                <MessageCircle size={13} /> WhatsApp
              </a>
            </div>
          </Detail>
          <Detail label="Email">
            <a href={`mailto:${enquiry.email}`} className="text-navy hover:text-gold inline-flex items-center gap-2 break-all">
              <MailIcon size={14} /> {enquiry.email}
            </a>
          </Detail>
          <Detail label="Matter Type"><span className="text-charcoal">{enquiry.matter_type}</span></Detail>
          <Detail label="Message">
            <p className="text-charcoal/80 whitespace-pre-wrap leading-relaxed">{enquiry.message || "—"}</p>
          </Detail>
          <div className="grid grid-cols-2 gap-6">
            <Detail label="Page Source"><span className="text-charcoal/75 text-sm">{enquiry.page_source || "—"}</span></Detail>
            <Detail label="Submitted">
              <div className="text-charcoal/75 text-sm">{dt.toLocaleString("en-IN")}</div>
            </Detail>
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/60 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="admin-drawer-status"
              className="w-full bg-ivory/60 border border-soft-grey px-3 py-2.5 text-sm outline-none focus:border-navy"
            >
              {STATUS_VALUES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-charcoal/60 mb-2">Admin Notes</label>
            <textarea
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-drawer-notes"
              className="w-full bg-ivory/60 border border-soft-grey px-3 py-2.5 text-sm outline-none focus:border-navy resize-none"
              placeholder="Internal notes about follow-up, next steps, fee discussions, etc."
            />
          </div>
        </div>

        <div className="px-7 py-5 border-t border-soft-grey flex flex-wrap items-center gap-3 justify-between sticky bottom-0 bg-white">
          <button
            onClick={remove}
            disabled={deleting}
            data-testid="admin-drawer-delete"
            className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-red-700 hover:text-red-900 transition-colors disabled:opacity-50"
          >
            <Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="btn-outline" data-testid="admin-drawer-cancel">Close</button>
            <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="admin-drawer-save">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

const Detail = ({ label, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.22em] uppercase text-charcoal/55 mb-1.5">{label}</div>
    {children}
  </div>
);
